import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons/faGoogleDrive";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { integrationActions } from "../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { openGoogleDrivePicker } from "../utils/integration-utils";

type Props = {
  open: boolean;
  onClose: () => void;
};

export const IntegrationAddModalGoogleDrive: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const [loading, setLoading] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const messageHandlerRef = useRef<
    ((event: MessageEvent) => void | Promise<void>) | null
  >(null);

  const cleanupListener = useCallback(() => {
    if (messageHandlerRef.current) {
      window.removeEventListener("message", messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
  }, []);

  useEffect(() => cleanupListener, [cleanupListener]);

  const finishConnecting = useCallback(
    async (code: string) => {
      try {
        const { _id, accessToken } = await dispatch(
          integrationActions.googleDriveOAuthCallback({ userId, code }),
        ).unwrap();

        // Opening the picker itself is best-effort: the connection is
        // already saved, and the section's "Add folders" button reopens
        // this exact step later if it's skipped or the picker fails to
        // load. But once the user has actually picked something, failing
        // to save it is a real error worth surfacing - silently dropping
        // it would show "connected" while quietly losing their selection.
        if (accessToken) {
          let roots: Awaited<ReturnType<typeof openGoogleDrivePicker>> = [];

          try {
            roots = await openGoogleDrivePicker(accessToken);
          } catch (pickerError) {
            console.log(pickerError);
          }

          if (roots.length) {
            try {
              await dispatch(
                integrationActions.registerGoogleDrivePickerRoots({
                  userId,
                  integrationId: _id,
                  roots,
                }),
              ).unwrap();
            } catch (rootsError) {
              AtiraToast.apiError(rootsError);
            }
          }
        }

        AtiraToast.success(
          t("integration.add-modal.google-drive.modal.connected"),
        );
        onClose();
      } catch (error) {
        AtiraToast.apiError(error);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, onClose, t, userId],
  );

  const handlePopupMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === "GOOGLE_DRIVE_CONNECT_SUCCESS") {
        cleanupListener();
        popupRef.current?.close();
        await finishConnecting(event.data.code);
      }
    },
    [cleanupListener, finishConnecting],
  );

  const onConnect = async () => {
    try {
      setLoading(true);

      const url = await dispatch(
        integrationActions.createGoogleDriveAuthLink({ userId }),
      ).unwrap();

      const popup = window.open(
        url,
        "google-drive-connect",
        "width=900,height=700,left=200,top=100",
      );

      if (!popup) {
        AtiraToast.error(t("common.popupBlocked"));
        setLoading(false);
        return;
      }

      popupRef.current = popup;
      messageHandlerRef.current = handlePopupMessage;
      window.addEventListener("message", handlePopupMessage);

      const popupCheckInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupCheckInterval);
          cleanupListener();
          setLoading(false);
        }
      }, 500);
    } catch (error) {
      AtiraToast.apiError(error);
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("integration.add-modal.google-drive.modal.title")}
      width={"30rem"}
    >
      <Flex flexDirection="column" alignItems="center" gap="l">
        <AtiraIcon icon={faGoogleDrive} size="4x" color="googleDrive" />

        <Text align="center">
          {t("integration.add-modal.google-drive.modal.description")}
        </Text>

        <Button
          icon={faGoogleDrive}
          loading={loading}
          loadingColor="white"
          onClick={onConnect}
          hover
        >
          {t("integration.add-modal.google-drive.modal.connect")}
        </Button>
      </Flex>
    </Modal>
  );
};
